import { atom } from "jotai";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { PromotionRepository } from "@/infrastructure/repositories/promotion/Promotion.repository";
import {
  promotionProductsAtom,
  promotionProductsCurrentPageAtom,
  promotionProductsFilterInfoAtom,
  promotionProductsPageLimitAtom
} from "./promotionProducts.atoms";
import {
  PromotionProductsFilterInfo,
  resolvePromotionProductsSortParams,
} from "./promotionProductsFilter.utils";
import { GetPromoProductsByPromoEventIdUseCase } from "@/core/usecases/promotion";
import { PromoProduct } from "@/core/entities/promotion";

const client = GraphQlAdapter.getInstance().getClient();
const promotionRepository = new PromotionRepository(client);

export const getPromoProductsByPromoEventIdAtom = atom(
  null,
  async (
    get,
    set,
    {
      promoEventId,
      isLoading = false,
      isRefreshing = false,
    }: { promoEventId: number; isLoading?: boolean; isRefreshing?: boolean }
  ) => {
    set(promotionProductsAtom, prev => ({ ...prev, isLoading, isRefreshing, error: null }));
    try {
      const filterInfo = get(promotionProductsFilterInfoAtom);
      const currentPage = get(promotionProductsCurrentPageAtom);
      const pageLimit = get(promotionProductsPageLimitAtom);
      const { sortColumn, sortOrder } = resolvePromotionProductsSortParams(
        filterInfo.sortColumn,
        filterInfo.sortOrder
      );

      const getPromoProductsByPromoEventIdUseCase = new GetPromoProductsByPromoEventIdUseCase(promotionRepository);
      const response = await getPromoProductsByPromoEventIdUseCase.execute(
        {
          promo_event_id: promoEventId,
          search: filterInfo.search ?? "",
          page_number: currentPage,
          page_size: pageLimit,
          sort_column: sortColumn,
          sort_order: sortOrder,
        }
      );

      set(promotionProductsAtom, prev => ({
        ...prev,
        promoProducts:  response.data.map(
          p => new PromoProduct(p.promoProductId, p.promotedPrice, p.discountPercentage, p.discountAmount, p.packQuantity, p.unitSize, p.unitOfMeasure, p.product)
        ),
        columns: response.columns,
        isLoading: false,
        isRefreshing: false,
        error: null,
        currentPage: currentPage,
        total: response.count,
        isAllProductsInScope: response.isAllProductsInScope
      }));
    } catch (error) {
      set(promotionProductsAtom, prev => ({
        ...prev,
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      }));
    }
  }
);

export const setPromotionProductsFilterAtom = atom(
  null,
  (get, set, filter: PromotionProductsFilterInfo) => {
    set(promotionProductsAtom, {
      ...get(promotionProductsAtom),
      filterInfo: filter,
    });
  }
);

export const setPromotionProductsCurrentPageAtom = atom(null, (get, set, page: number) => {
  set(promotionProductsAtom, {
    ...get(promotionProductsAtom),
    currentPage: page,
  });
});
